import * as React from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { useAnalysisReports } from "@/api/analysis-report";
import { ApiError } from "@/api/client";
import { useProject, useProjectRuns } from "@/api/projects";
import { ANALYZABLE_RUN_STATES } from "@/api/runs";
import { EmptyState } from "@/components/layout/EmptyState";
import { ErrorBoundary } from "@/components/layout/ErrorBoundary";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { apiErrorMessage, classifyApiError } from "@/lib/api-error-kind";
import {
  availableTorsionIds,
  buildOverlay,
  overlayLinkFor,
  overlaySearchParams,
  parseOverlayLink,
  MAX_OVERLAY_RUNS,
  MAX_OVERLAY_SERIES,
  OVERLAY_SERIES_KINDS,
  SERIES_KIND_LABEL,
  type OverlayRunState,
  type OverlaySelection,
  type OverlaySeriesKind,
} from "@/lib/torsion-overlay";

/**
 * Lazy boundary for the overlay chart, following `MoleculeViewerPanel` and the
 * reactflow DAG canvas — this codebase's one idiom for a heavy widget, not a
 * second one. The `import()` is the only route into `TorsionOverlayChart`, so
 * the comparison chart is fetched when a user opens a comparison and is not
 * linked into the chunk any other route pulls.
 *
 * This boundary alone was not enough, and the reason is worth keeping: while
 * `RunDetail` and `AnalysisRunView` still imported `AnalysisTab` statically
 * (and through it `DihedralProfilePlot`), recharts sat in the entry chunk
 * regardless, so this chunk was small only because the library was already
 * paid for on every route. flowstack#992 put the per-run path behind
 * boundaries too; the invariant is that EVERY importer must be behind one,
 * which `components/affdo/chart-lazy-boundary.test.ts` now enforces.
 */
const TorsionOverlayChart = React.lazy(() => import("@/components/affdo/TorsionOverlayChart"));

interface RouteParams {
  slug?: string;
  [key: string]: string | undefined;
}

/**
 * Cross-run torsion comparison (VISUALIZATION_PLAN U2.1, the remaining half)
 * — `/analysis/:slug/compare`.
 *
 * **The URL is the whole selection.** There is no component state mirroring
 * it: `runs`, `torsions` and `series` are read from and written to the query
 * string on every change, so the address bar always names exactly what is on
 * screen and "send someone this comparison" is copying it. That is also why
 * the picker below never rewrites a run id it does not recognise — a link may
 * legitimately name a run this project's list does not contain.
 *
 * **The shared link describes the RENDERED view** (flowstack#994 item 1).
 * An arriving link can name more than one comparison draws, and #993 then
 * copied it back out verbatim — so the recipient saw something different from
 * what the sender was looking at, twice over. Of the two ways out ("the link
 * describes the view" or "the view says the link was rewritten"), this surface
 * takes the first for what it hands over and the second for what it displays:
 * **Copy link** copies the narrowed selection, the notice says so, and the
 * address bar is left holding the wider link because that is the evidence the
 * notice is talking about.
 *
 * **One budget, not two caps.** `MAX_OVERLAY_SERIES` is the authoritative
 * limit and the torsion limit is derived from it for the runs actually
 * selected, so a selection can no longer clear the list caps and then be cut
 * by the draw limit with a different message (flowstack#994 item 2). The
 * pickers refuse a click past the budget with a reason, rather than accepting
 * one the URL round-trip would silently drop.
 *
 * **A link that names a run the viewer cannot see renders a PARTIAL overlay
 * plus an explicit account of what is missing** — never a silent short chart.
 * The plan calls out 404, empty, and partial as all defensible, and the one
 * unacceptable outcome as dropping a series with nothing saying why. Partial
 * is chosen because the common case for a broken link is one stale run id out
 * of several, and refusing the whole comparison would throw away the runs the
 * user CAN see over a run they may not even care about. Every unplotted
 * (run, torsion) pair is listed underneath with its reason — not found, no
 * permission, report not generated, no profile for that torsion, or no curve
 * of the selected kind.
 *
 * **Materialise-on-demand is respected**: this surface reads reports and never
 * generates them. A run without one reads as "not generated yet" with a link
 * to the run's own analysis, which is where the Generate action lives — it is
 * emphatically not "this run has no data".
 */
export function AnalysisComparePage(): JSX.Element {
  const { slug = "" } = useParams<RouteParams>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const project = useProject(slug);
  const runs = useProjectRuns(slug);

  const link = React.useMemo(() => parseOverlayLink(searchParams), [searchParams]);
  const selection = link.selection;

  const setSelection = React.useCallback(
    (next: OverlaySelection) => {
      // `replace`: a comparison is refined by clicking, and one Back press
      // should leave the surface rather than walk back through every toggle.
      setSearchParams(overlaySearchParams(next), { replace: true });
    },
    [setSearchParams]
  );

  const reports = useAnalysisReports(selection.runIds);

  // Not memoised, deliberately: `useQueries` hands back a fresh array on
  // every render, so a `useMemo` keyed on it would recompute every time
  // anyway while reading as though it did not. The work is a walk over a
  // handful of reports' profile arrays — cheap, and honest about being so.
  const runStates = new Map<string, OverlayRunState>();
  selection.runIds.forEach((runId, index) => {
    const query = reports[index];
    if (!query || query.isPending) {
      runStates.set(runId, { kind: "loading" });
      return;
    }
    if (query.isError) {
      runStates.set(runId, {
        kind: "unreadable",
        errorKind: classifyApiError(query.error),
        message: apiErrorMessage(query.error),
      });
      return;
    }
    const envelope = query.data;
    if (!envelope?.available || !envelope.report) {
      runStates.set(runId, { kind: "not-generated", reason: envelope?.reason ?? null });
      return;
    }
    runStates.set(runId, { kind: "ready", report: envelope.report });
  });

  const torsionOptions = availableTorsionIds(runStates.values());

  // Run ids are labelled in FULL (no `runLabel` override). Truncating them
  // looked tidier until you notice that two runs of one project share a long
  // leading prefix — a shortened legend would render them identically, which
  // is the one thing a comparison's legend must never do.
  const model = buildOverlay({ selection, runs: runStates });

  const analyzableRuns = (runs.data ?? []).filter((run) => ANALYZABLE_RUN_STATES.has(run.status));
  // A link can name runs that are not in this project's list — a run from
  // another project, a deleted one, or one the viewer cannot see. They stay
  // selected and are reported below rather than being quietly pruned out of
  // the URL the sender shared.
  const unlistedRunIds = selection.runIds.filter(
    (id) => !analyzableRuns.some((run) => run.run_id === id)
  );

  const toggle = (list: string[], value: string): string[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  const runsAtCap = selection.runIds.length >= MAX_OVERLAY_RUNS;
  const torsionsAtCap = selection.torsionIds.length >= link.torsionLimit;

  // The link for what is ON SCREEN, not the one that arrived (flowstack#994
  // item 1). `location.pathname` rather than `window.location.pathname`
  // because the router owns the path; the origin is the browser's.
  const shareUrl = overlayLinkFor(selection, `${window.location.origin}${location.pathname}`);
  const wasNarrowed = link.droppedRunIds.length > 0 || link.droppedTorsionIds.length > 0;

  const copyLink = (): void => {
    // Same defensive shape as `Settings.tsx` (Rupert PR-270 r0):
    // `navigator.clipboard` is undefined on insecure origins and in jsdom.
    if (!navigator.clipboard?.writeText) {
      toast.error("Copying is unavailable here — copy the address bar instead.");
      return;
    }
    navigator.clipboard.writeText(shareUrl).then(
      () =>
        toast.success(
          wasNarrowed
            ? "Copied a link to this comparison — narrowed to what is drawn here"
            : "Comparison link copied"
        ),
      () => toast.error("Failed to copy the link")
    );
  };

  const breadcrumbs = (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Link to="/analysis" className="hover:text-foreground">
        Analysis
      </Link>
      <span aria-hidden>·</span>
      <Link to={`/analysis/${slug}`} className="font-mono hover:text-foreground">
        {slug}
      </Link>
      <span aria-hidden>·</span>
      <span>Compare</span>
    </div>
  );

  if (project.isError) {
    const isNotFound = project.error instanceof ApiError && project.error.status === 404;
    return (
      <div className="flex flex-col gap-6">
        {breadcrumbs}
        <EmptyState
          title={isNotFound ? "Project not found." : "Could not load project."}
          description={
            isNotFound
              ? `No project with slug ${slug ? `'${slug}'` : "(empty)"}.`
              : apiErrorMessage(project.error)
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {breadcrumbs}
      <PageHeader
        title="Compare torsion profiles"
        description="Put two or more runs' torsion profiles on the same axes. The whole selection lives in the link — Copy link sends someone exactly the view on this screen."
        actions={
          <Button variant="outline" size="sm" onClick={copyLink}>
            Copy link
          </Button>
        }
      />

      {/* A link may ask for more than this surface will draw. One budget
          governs that — MAX_OVERLAY_SERIES curves — and the run cap bounds a
          different resource (one authenticated request per run), so both are
          named here rather than left to be discovered as two unrelated rules
          (flowstack#994 item 2). A silent cap would be the very defect the
          "Not plotted" block exists to prevent, so say it up here, where it
          explains the whole view rather than one missing curve. */}
      {wasNarrowed && (
        <p
          role="status"
          className="rounded-md border-l-4 border-state-failed-validation-fg bg-state-failed-validation-fg/5 px-4 py-3 text-xs text-foreground"
          data-testid="overlay-link-truncated"
        >
          This comparison shows less than the selection asked for.{" "}
          {link.droppedRunIds.length > 0 &&
            `It names ${selection.runIds.length + link.droppedRunIds.length} runs; the first ${MAX_OVERLAY_RUNS} are used. `}
          {link.droppedTorsionIds.length > 0 &&
            `It names ${selection.torsionIds.length + link.droppedTorsionIds.length} torsions; the first ${link.torsionLimit} are used, because ${selection.runIds.length} run(s) x ${link.torsionLimit} torsions is this chart's limit of ${MAX_OVERLAY_SERIES} curves. `}
          <strong className="font-medium">Copy link</strong> copies the narrowed comparison, so what
          you share is what is drawn here; the address bar still holds the wider selection.
        </p>
      )}

      <section className="grid gap-4 lg:grid-cols-3" aria-label="Comparison selection">
        <Card as="fieldset" className="flex flex-col gap-2 p-3">
          <legend className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Runs
          </legend>
          {runs.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : analyzableRuns.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              This project has no analyzable runs yet.{" "}
              <Link to={`/analysis/${slug}`} className="text-cyan hover:underline">
                Back to its run list →
              </Link>
            </p>
          ) : (
            <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto">
              {analyzableRuns.map((run) => {
                const checked = selection.runIds.includes(run.run_id);
                return (
                  <li key={run.run_id}>
                    <label
                      className={`flex items-center gap-2 rounded px-1 py-1 text-xs hover:bg-surface-2 ${
                        !checked && runsAtCap ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                      }`}
                      title={
                        !checked && runsAtCap
                          ? `At most ${MAX_OVERLAY_RUNS} runs at a time — unpick one first.`
                          : undefined
                      }
                    >
                      <input
                        type="checkbox"
                        className="accent-cyan"
                        checked={checked}
                        // The caps are enforced when the URL is read, so a
                        // click past one would be dropped on the way back in
                        // and read as a checkbox that does not work. Refusing
                        // it here, with a reason, is the same rule stated
                        // where the user is standing (flowstack#994 item 2).
                        disabled={!checked && runsAtCap}
                        onChange={() =>
                          setSelection({
                            ...selection,
                            runIds: toggle(selection.runIds, run.run_id),
                          })
                        }
                      />
                      <span className="truncate font-mono">{run.run_id}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          {unlistedRunIds.length > 0 && (
            <p className="px-1 text-[11px] text-muted-foreground">
              {unlistedRunIds.length} run(s) in this link are not in this project&rsquo;s list; they
              are still requested and reported below.
            </p>
          )}
        </Card>

        <Card as="fieldset" className="flex flex-col gap-2 p-3">
          <legend className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Torsions
          </legend>
          {selection.runIds.length === 0 ? (
            <p className="text-xs text-muted-foreground">Pick a run first.</p>
          ) : model.loading ? (
            <Skeleton className="h-24 w-full" />
          ) : torsionOptions.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              None of the selected runs offers a torsion profile.
            </p>
          ) : (
            <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto">
              {torsionOptions.map((torsionId) => {
                const checked = selection.torsionIds.includes(torsionId);
                return (
                  <li key={torsionId}>
                    <label
                      className={`flex items-center gap-2 rounded px-1 py-1 text-xs hover:bg-surface-2 ${
                        !checked && torsionsAtCap
                          ? "cursor-not-allowed opacity-50"
                          : "cursor-pointer"
                      }`}
                      title={
                        !checked && torsionsAtCap
                          ? `${selection.runIds.length} run(s) x ${link.torsionLimit} torsions already fills this chart's ${MAX_OVERLAY_SERIES}-curve limit.`
                          : undefined
                      }
                    >
                      <input
                        type="checkbox"
                        className="accent-cyan"
                        checked={checked}
                        disabled={!checked && torsionsAtCap}
                        onChange={() =>
                          setSelection({
                            ...selection,
                            torsionIds: toggle(selection.torsionIds, torsionId),
                          })
                        }
                      />
                      <span className="truncate font-mono">{torsionId}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          {/* The one budget, stated where it bites: the run and torsion lists
              are not independent limits, they spend the same
              MAX_OVERLAY_SERIES curves. */}
          {selection.runIds.length > 0 && (
            <p
              className="px-1 text-[11px] text-muted-foreground"
              data-testid="overlay-curve-budget"
            >
              This chart draws at most {MAX_OVERLAY_SERIES} curves — with {selection.runIds.length}{" "}
              run(s) picked, that is {link.torsionLimit} torsion(s).
            </p>
          )}
        </Card>

        <Card as="fieldset" className="flex flex-col gap-2 p-3">
          <legend className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Curve
          </legend>
          {/* One series kind at a time. Overlaying QM, MM and fit for several
              runs at once puts three curves per run on one axis, where the
              question "is run A's fit better than run B's" becomes harder to
              read, not easier. The kind is in the URL, so switching it is
              still one click and still shareable. */}
          <div className="flex flex-col gap-1">
            {OVERLAY_SERIES_KINDS.map((kind: OverlaySeriesKind) => (
              <label
                key={kind}
                className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs hover:bg-surface-2"
              >
                <input
                  type="radio"
                  className="accent-cyan"
                  name="overlay-series"
                  value={kind}
                  checked={selection.series === kind}
                  onChange={() => setSelection({ ...selection, series: kind })}
                />
                <span>{SERIES_KIND_LABEL[kind]}</span>
              </label>
            ))}
          </div>
          <p className="px-1 text-[11px] text-muted-foreground">
            Every curve is relative to its own minimum, so the axis compares shapes and barrier
            heights — not absolute energies.
          </p>
        </Card>
      </section>

      <section aria-label="Overlay" className="flex flex-col gap-3">
        {model.loading && model.series.length === 0 ? (
          <Skeleton className="h-[420px] w-full" />
        ) : model.series.length === 0 ? (
          <EmptyState
            title="Nothing to overlay yet."
            description={
              selection.runIds.length === 0 || selection.torsionIds.length === 0
                ? "Pick at least two runs and a torsion to compare. Two runs and two torsions is the view this surface is built for."
                : "None of the selected runs published a curve of this kind for the selected torsions. The reasons are listed below."
            }
          />
        ) : (
          <Card className="p-3">
            {model.angleGridsDiffer && (
              <p
                role="status"
                className="pb-2 text-[11px] text-state-failed-scientific-fg"
                data-testid="overlay-grid-mismatch-note"
              >
                These curves were not all evaluated on the same angle grid. They are drawn on shared
                axes anyway; compare shapes rather than reading point-for-point differences.
              </p>
            )}
            {/* A third-party chart that throws on an odd payload must cost
                this panel, not the page — same rule `ErrorBoundary`'s own
                docstring states and `MoleculeViewerPanel` follows. */}
            <ErrorBoundary
              fallback={() => (
                <p className="text-sm text-muted-foreground">
                  The overlay could not be rendered. The selection is still in the address bar.
                </p>
              )}
            >
              <React.Suspense fallback={<Skeleton className="h-[420px] w-full" />}>
                <TorsionOverlayChart model={model} seriesKind={selection.series} />
              </React.Suspense>
            </ErrorBoundary>
            {/* The legend lives here rather than inside the chart: recharts'
                own <Legend> is SVG a screen reader cannot reach, and it is
                the one part of a chart that carries meaning as text. It also
                stays readable when the chart itself does not render. */}
            <ul
              className="flex flex-wrap gap-x-4 gap-y-1 pt-2"
              data-testid="overlay-legend"
              aria-label={`${model.series.length} curve(s) plotted`}
            >
              {model.series.map((series) => (
                <li key={series.key} className="flex items-center gap-1.5 text-[11px]">
                  <span
                    className="inline-block h-0.5 w-4 rounded"
                    style={{ background: series.color }}
                    aria-hidden
                  />
                  <span className="font-mono text-muted-foreground">{series.label}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {model.gaps.length > 0 && (
          <div
            className="flex flex-col gap-2 rounded-lg border border-border-subtle bg-surface-2/40 p-3"
            data-testid="overlay-gaps"
          >
            <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Not plotted ({model.gaps.length})
            </h2>
            {/* The point of this block: the chart may show fewer curves than
                the link named, and every one of them says why. */}
            <ul className="flex flex-col gap-1">
              {model.gaps.map((gap) => (
                // Keyed by the fragment too: a run can match one torsion in
                // several fragments, and two of them failing the same way are
                // two distinct lines, not one React key used twice.
                <li
                  key={`${gap.runId}:${gap.torsionId ?? "*"}:${gap.fragmentId ?? "*"}`}
                  className="text-xs text-muted-foreground"
                >
                  <Link
                    to={`/analysis/${slug}/${gap.runId}`}
                    className="font-mono text-foreground hover:underline"
                  >
                    {gap.runId}
                  </Link>
                  {gap.torsionId !== null && (
                    <>
                      {" · "}
                      <span className="font-mono">{gap.torsionId}</span>
                    </>
                  )}
                  {gap.fragmentId !== null && (
                    <>
                      {" "}
                      <span className="font-mono">({gap.fragmentId})</span>
                    </>
                  )}
                  {" — "}
                  {gap.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}
